/**
 * "Didn't expect this? Report it." — the email footer links here with
 * `&report=1`. Confirming revokes the invitation and writes an audit entry
 * (`POST /invitations/report/`, idempotent).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag, ShieldCheck } from "lucide-react";
import { Btn, Note, ObShell, TopBar, obErrorCode, reportInvitation } from "../kit";
import { StatusCard } from "./common";

export function ReportScreen({ token, onSetUp }: { token: string; onSetUp: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const report = async () => {
    setBusy(true);
    setError(null);
    try {
      await reportInvitation(token);
      setDone(true);
    } catch (err) {
      const c = obErrorCode(err);
      setError(
        c === "accepted"
          ? t(
              "onboarding.invitee.report.errAccepted",
              "This invitation was already used to create an account, so it can't be reported here. If that wasn't you, tell your organization's administrator.",
            )
          : c === "invalid" || c === "missing_token"
            ? t("onboarding.invitee.report.errInvalid", "This link isn't valid, so there is nothing to report.")
            : c === "rate_limited"
              ? t("onboarding.invitee.report.errRateLimited", "Too many attempts. Try again in an hour.")
              : t("onboarding.invitee.err.generic", "Check your connection and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <ObShell>
      <TopBar />
      {done ? (
        <StatusCard tone="ok" icon={ShieldCheck} title={t("onboarding.invitee.report.doneTitle", "Reported.")} label="Invitation reported">
          <p className="ob-p">
            {t(
              "onboarding.invitee.report.doneBody",
              "Our security team will review it. The link no longer works, so you can ignore the email.",
            )}
          </p>
        </StatusCard>
      ) : (
        <StatusCard
          icon={Flag}
          title={t("onboarding.invitee.report.title", "Report this invitation?")}
          label="Report invitation"
          actions={
            <>
              <Btn size="l" busy={busy} onClick={report}>
                {t("onboarding.invitee.report.confirm", "Report invitation")}
              </Btn>
              <Btn kind="ter" size="l" disabled={busy} onClick={onSetUp}>
                {t("onboarding.invitee.report.setUpInstead", "I expected it. Set up my account")}
              </Btn>
            </>
          }
        >
          <p className="ob-p">
            {t(
              "onboarding.invitee.report.body",
              "If you didn't expect an invitation to We-Co, report it. We'll cancel the link so nobody can use it, and our security team will review it.",
            )}
          </p>
          {error && <Note kind="bad">{error}</Note>}
        </StatusCard>
      )}
    </ObShell>
  );
}
